import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Hexagon, Mail, Lock, AlertCircle, Shield, ArrowLeft } from 'lucide-react';
interface LoginProps {
  onLogin: () => void;
}
export function Login({
  onLogin
}: LoginProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showMFA, setShowMFA] = useState(false);
  const [mfaCode, setMfaCode] = useState(['', '', '', '', '', '']);
  const [mfaError, setMfaError] = useState('');
  const navigate = useNavigate();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  useEffect(() => {
    if (showMFA && inputRefs.current[0]) {
      inputRefs.current[0]?.focus();
    }
  }, [showMFA]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    if (email === 'admin' && password === 'admin') {
      setShowMFA(true);
      setIsLoading(false);
    } else {
      setError('Ungültige Anmeldedaten. Versuchen Sie admin/admin');
      setIsLoading(false);
    }
  };
  const handleMFAChange = (index: number, value: string) => {
    if (value && !/^\d$/.test(value)) return;
    const newCode = [...mfaCode];
    newCode[index] = value;
    setMfaCode(newCode);
    setMfaError('');
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    if (index === 5 && value) {
      const fullCode = newCode.join('');
      verifyMFACode(fullCode);
    }
  };
  const handleMFAKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !mfaCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };
  const handleMFAPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split('');
      setMfaCode(digits);
      inputRefs.current[5]?.focus();
      verifyMFACode(pastedData);
    }
  };
  const verifyMFACode = async (code: string) => {
    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    if (code === '123456') {
      localStorage.setItem('isAuthenticated', 'true');
      onLogin();
      navigate('/');
    } else {
      setMfaError('Ungültiger Code. Versuchen Sie 123456');
      setMfaCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
      setIsLoading(false);
    }
  };
  const handleBackToLogin = () => {
    setShowMFA(false);
    setMfaCode(['', '', '', '', '', '']);
    setMfaError('');
    setPassword('');
  };
  const handleSocialLogin = async (provider: string) => {
    try {
      setIsLoading(true);
      await new Promise(resolve => setTimeout(resolve, 800));
      localStorage.setItem('isAuthenticated', 'true');
      onLogin();
      navigate('/');
    } catch (err) {
      console.error('Social login error:', err);
      setIsLoading(false);
    }
  };
  return <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4" data-id="element-394">
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMiI+PHBhdGggZD0iTTM2IDM0djItaDJ2LTJoLTJ6bTAtNGgydjJoLTJ2LTJ6bTAtNGgydjJoLTJ2LTJ6bTAtNGgydjJoLTJ2LTJ6bTAtNGgydjJoLTJ2LTJ6bTAtNGgydjJoLTJ2LTJ6bTAtNGgydjJoLTJ2LTJ6bTAtNGgydjJoLTJ2LTJ6bTAtNGgydjJoLTJ2LTJ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-40" data-id="element-395"></div>

      <div className="relative w-full max-w-md" data-id="element-396">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden" data-id="element-397">
          <div className="bg-gradient-to-r from-teal-600 to-teal-500 p-8 text-center" data-id="element-398">
            <div className="flex justify-center mb-4" data-id="element-399">
              <div className="p-3 bg-white/10 backdrop-blur-sm rounded-2xl" data-id="element-400">
                {showMFA ? <Shield className="h-12 w-12 text-white" data-id="element-401" /> : <Hexagon className="h-12 w-12 text-white fill-current" data-id="element-402" />}
              </div>
            </div>
            <h1 className="text-3xl font-bold text-white mb-1" data-id="element-403">neocube ONE</h1>
            <p className="text-teal-100 text-sm" data-id="element-404">
              {showMFA ? 'Zwei-Faktor-Authentifizierung' : 'IT Service Management Platform'}
            </p>
          </div>

          <div className="p-8" data-id="element-405">
            {!showMFA ? <>
                <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-406">
                  Willkommen zurück
                </h2>
                <p className="text-slate-500 text-sm mb-6" data-id="element-407">
                  Melden Sie sich an, um fortzufahren
                </p>

                {error && <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" data-id="element-408">
                    <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" data-id="element-409" />
                    <div data-id="element-410">
                      <p className="text-sm font-medium text-red-900" data-id="element-411">
                        Anmeldung fehlgeschlagen
                      </p>
                      <p className="text-sm text-red-700 mt-0.5" data-id="element-412">{error}</p>
                    </div>
                  </div>}

                <form onSubmit={handleSubmit} className="space-y-4" data-id="element-413">
                  <Input type="text" label="E-Mail oder Benutzername" placeholder="admin" value={email} onChange={e => setEmail(e.target.value)} icon={<Mail className="h-4 w-4" data-id="element-415" />} required disabled={isLoading} data-id="element-414" />

                  <Input type="password" label="Passwort" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} icon={<Lock className="h-4 w-4" data-id="element-417" />} required disabled={isLoading} data-id="element-416" />

                  <div className="flex items-center justify-between text-sm" data-id="element-418">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-419">
                      <input type="checkbox" className="rounded border-slate-300 text-teal-600 focus:ring-teal-500" disabled={isLoading} data-id="element-420" />
                      <span className="text-slate-600" data-id="element-421">Angemeldet bleiben</span>
                    </label>
                    <a href="#" className="text-teal-600 hover:text-teal-700 font-medium" data-id="element-422">
                      Passwort vergessen?
                    </a>
                  </div>

                  <Button type="submit" className="w-full" size="lg" isLoading={isLoading} data-id="element-423">
                    Anmelden
                  </Button>
                </form>

                <div className="relative my-6" data-id="element-424">
                  <div className="absolute inset-0 flex items-center" data-id="element-425">
                    <div className="w-full border-t border-slate-200" data-id="element-426"></div>
                  </div>
                  <div className="relative flex justify-center text-sm" data-id="element-427">
                    <span className="px-4 bg-white text-slate-500" data-id="element-428">
                      Oder anmelden mit
                    </span>
                  </div>
                </div>

                <div className="space-y-3" data-id="element-429">
                  <button type="button" onClick={() => handleSocialLogin('google')} disabled={isLoading} className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors group disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-430">
                    <svg className="h-5 w-5" viewBox="0 0 24 24" data-id="element-431">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" data-id="element-432" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" data-id="element-433" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" data-id="element-434" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" data-id="element-435" />
                    </svg>
                    <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900" data-id="element-436">
                      Mit Google anmelden
                    </span>
                  </button>

                  <button type="button" onClick={() => handleSocialLogin('microsoft')} disabled={isLoading} className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors group disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-437">
                    <svg className="h-5 w-5" viewBox="0 0 23 23" data-id="element-438">
                      <path fill="#f3f3f3" d="M0 0h23v23H0z" data-id="element-439" />
                      <path fill="#f35325" d="M1 1h10v10H1z" data-id="element-440" />
                      <path fill="#81bc06" d="M12 1h10v10H12z" data-id="element-441" />
                      <path fill="#05a6f0" d="M1 12h10v10H1z" data-id="element-442" />
                      <path fill="#ffba08" d="M12 12h10v10H12z" data-id="element-443" />
                    </svg>
                    <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900" data-id="element-444">
                      Mit Microsoft anmelden
                    </span>
                  </button>

                  <button type="button" onClick={() => handleSocialLogin('apple')} disabled={isLoading} className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors group disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-445">
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" data-id="element-446">
                      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" data-id="element-447" />
                    </svg>
                    <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900" data-id="element-448">
                      Mit Apple anmelden
                    </span>
                  </button>
                </div>

                <div className="mt-6 p-4 bg-teal-50 border border-teal-100 rounded-lg" data-id="element-449">
                  <p className="text-xs text-teal-800 text-center" data-id="element-450">
                    <span className="font-semibold" data-id="element-451">Demo-Zugang:</span> admin /
                    admin
                  </p>
                </div>
              </> : <>
                <button onClick={handleBackToLogin} className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-6 transition-colors" data-id="element-452">
                  <ArrowLeft className="h-4 w-4" data-id="element-453" />
                  Zurück zur Anmeldung
                </button>

                <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-454">
                  Bestätigungscode eingeben
                </h2>
                <p className="text-slate-500 text-sm mb-8" data-id="element-455">
                  Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein
                </p>

                {mfaError && <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" data-id="element-456">
                    <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" data-id="element-457" />
                    <div data-id="element-458">
                      <p className="text-sm font-medium text-red-900" data-id="element-459">
                        Ungültiger Code
                      </p>
                      <p className="text-sm text-red-700 mt-0.5" data-id="element-460">{mfaError}</p>
                    </div>
                  </div>}

                <div className="flex gap-3 justify-center mb-8" data-id="element-461">
                  {mfaCode.map((digit, index) => <input key={index} ref={el => inputRefs.current[index] = el} type="text" inputMode="numeric" maxLength={1} value={digit} onChange={e => handleMFAChange(index, e.target.value)} onKeyDown={e => handleMFAKeyDown(index, e)} onPaste={index === 0 ? handleMFAPaste : undefined} disabled={isLoading} className={`
                        w-12 h-14 text-center text-2xl font-bold border-2 rounded-lg
                        focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent
                        transition-all duration-200
                        ${mfaError ? 'border-red-300 bg-red-50' : 'border-slate-300'}
                        ${digit ? 'border-teal-500 bg-teal-50' : ''}
                        disabled:opacity-50 disabled:cursor-not-allowed
                      `} data-id="element-462" />)}
                </div>

                <Button onClick={() => verifyMFACode(mfaCode.join(''))} className="w-full" size="lg" isLoading={isLoading} disabled={mfaCode.some(d => !d)} data-id="element-463">
                  Code bestätigen
                </Button>

                <div className="mt-6 text-center" data-id="element-464">
                  <button className="text-sm text-teal-600 hover:text-teal-700 font-medium" data-id="element-465">
                    Code erneut senden
                  </button>
                </div>

                <div className="mt-6 p-4 bg-teal-50 border border-teal-100 rounded-lg" data-id="element-466">
                  <p className="text-xs text-teal-800 text-center" data-id="element-467">
                    <span className="font-semibold" data-id="element-468">Demo-Code:</span> 123456
                  </p>
                </div>
              </>}
          </div>
        </div>

        <p className="text-center text-sm text-slate-400 mt-6" data-id="element-469">
          © 2023 neocube GmbH. Alle Rechte vorbehalten.
        </p>
      </div>
    </div>;
}